<template>
  <div class="estrut-admin">
    <p class="estrut-admin__label">{{ t('estruturacao.lessons.adminPanel') }}</p>
    <div class="estrut-admin__btns">
      <button type="button" class="estrut-admin__btn" @click="openCreateCategory">
        + {{ t('estruturacao.lessons.newCategory') }}
      </button>
    </div>

    <EstruturacaoCategoriaModal
      ref="categoriaModalRef"
      :open="catModal.open"
      :mode="catModal.mode"
      :initial="catModal.initial"
      @close="closeCatModal"
      @submit="onCatSubmit"
    />

    <EstruturacaoAulaModal
      ref="aulaModalRef"
      :open="aulaModal.open"
      :mode="aulaModal.mode"
      :categorias="tree?.categorias || []"
      :initial="aulaModal.initial"
      :default-categoria-id="aulaModal.defaultCategoriaId"
      :lock-categoria="aulaModal.lockCategoria"
      @close="closeAulaModal"
      @submit="onAulaSubmit"
    />

    <EstruturacaoConfirmModal
      :open="confirm.open"
      :title="confirm.title"
      :message="confirm.message"
      :loading="confirm.loading"
      :single-action="confirm.type === 'info'"
      @cancel="confirm.open = false"
      @confirm="onConfirmDelete"
    />
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  createCategoria,
  updateCategoria,
  deleteCategoria,
  createAula,
  updateAula,
  deleteAula,
  fetchAula
} from '../../services/estruturacaoApi'
import EstruturacaoCategoriaModal from './EstruturacaoCategoriaModal.vue'
import EstruturacaoAulaModal from './EstruturacaoAulaModal.vue'
import EstruturacaoConfirmModal from './EstruturacaoConfirmModal.vue'

const props = defineProps({
  modulo: { type: String, required: true },
  tree: { type: Object, default: null }
})

const emit = defineEmits(['refresh'])

const { t } = useI18n()

const categoriaModalRef = ref(null)
const aulaModalRef = ref(null)

const catModal = reactive({
  open: false,
  mode: 'create',
  initial: null,
  editId: null
})

const aulaModal = reactive({
  open: false,
  mode: 'create',
  initial: null,
  editId: null,
  defaultCategoriaId: null,
  lockCategoria: false
})

const confirm = reactive({
  open: false,
  title: '',
  message: '',
  loading: false,
  type: null,
  target: null
})

function apiError(e) {
  return e.response?.data?.error || t('estruturacao.lessons.saveError')
}

function openCreateCategory() {
  catModal.mode = 'create'
  catModal.initial = null
  catModal.editId = null
  catModal.open = true
}

function openEditCategory(cat) {
  catModal.mode = 'edit'
  catModal.initial = { titulo: cat.titulo, ativo: cat.ativo }
  catModal.editId = cat.id
  catModal.open = true
}

function closeCatModal() {
  catModal.open = false
  categoriaModalRef.value?.setSaving(false)
}

async function onCatSubmit(data) {
  try {
    if (catModal.mode === 'create') {
      await createCategoria(props.modulo, data)
    } else {
      await updateCategoria(catModal.editId, data)
    }
    closeCatModal()
    emit('refresh')
  } catch (e) {
    categoriaModalRef.value?.setError(apiError(e))
  }
}

function openCreateLesson(defaultCategoriaId = null) {
  const categorias = props.tree?.categorias || []
  if (!categorias.length) {
    confirm.type = 'info'
    confirm.target = null
    confirm.title = t('common.error')
    confirm.message = t('estruturacao.lessons.needCategoryFirst')
    confirm.loading = false
    confirm.open = true
    return
  }
  aulaModal.mode = 'create'
  aulaModal.initial = null
  aulaModal.editId = null
  aulaModal.defaultCategoriaId = defaultCategoriaId || categorias[0]?.id
  aulaModal.lockCategoria = defaultCategoriaId != null
  aulaModal.open = true
}

async function openEditAula(aula, categoriaId) {
  aulaModal.mode = 'edit'
  aulaModal.editId = aula.id
  aulaModal.defaultCategoriaId = categoriaId
  aulaModal.lockCategoria = false

  let initial = {
    titulo: aula.titulo,
    youtubeUrl: '',
    descricao: '',
    ativo: aula.ativo !== false,
    categoriaId
  }
  try {
    const { data } = await fetchAula(aula.id)
    initial = {
      titulo: data.titulo,
      youtubeUrl: data.youtubeUrl || '',
      descricao: data.descricao || '',
      ativo: data.ativo != null ? data.ativo !== false : aula.ativo !== false,
      categoriaId
    }
  } catch {
    // mantém dados da árvore (inclui ativo correto)
  }

  aulaModal.initial = initial
  aulaModal.open = true
}

function closeAulaModal() {
  aulaModal.open = false
  aulaModal.lockCategoria = false
  aulaModalRef.value?.setSaving(false)
}

async function onAulaSubmit(data) {
  try {
    if (aulaModal.mode === 'create') {
      const { categoriaId, ...rest } = data
      await createAula(categoriaId, rest)
    } else {
      const { categoriaId, ...rest } = data
      await updateAula(aulaModal.editId, rest)
    }
    closeAulaModal()
    emit('refresh')
  } catch (e) {
    aulaModalRef.value?.setError(apiError(e))
  }
}

function openDeleteCategory(cat) {
  confirm.type = 'category'
  confirm.target = cat
  confirm.title = t('estruturacao.lessons.deleteConfirmTitle')
  confirm.message = t('estruturacao.lessons.deleteCategoryConfirm') + ` (${cat.titulo})`
  confirm.loading = false
  confirm.open = true
}

function openDeleteAula(aula) {
  confirm.type = 'lesson'
  confirm.target = aula
  confirm.title = t('estruturacao.lessons.deleteConfirmTitle')
  confirm.message = t('estruturacao.lessons.deleteLessonConfirm') + ` (${aula.titulo})`
  confirm.loading = false
  confirm.open = true
}

async function onConfirmDelete() {
  if (confirm.type === 'info') {
    confirm.open = false
    return
  }
  confirm.loading = true
  try {
    if (confirm.type === 'category') {
      await deleteCategoria(confirm.target.id)
    } else if (confirm.type === 'lesson') {
      await deleteAula(confirm.target.id)
    }
    confirm.open = false
    emit('refresh')
  } catch (e) {
    confirm.message = e.response?.data?.error || t('estruturacao.lessons.deleteError')
  } finally {
    confirm.loading = false
  }
}

defineExpose({
  openCreateCategory,
  openCreateLesson,
  openEditCategory,
  openEditAula,
  openDeleteCategory,
  openDeleteAula
})
</script>

<style scoped>
.estrut-admin {
  padding: 12px;
  border-radius: 4px;
  border: 1px dashed var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 6%, transparent);
}

.estrut-admin__label {
  margin: 0 0 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent-primary);
}

.estrut-admin__btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.estrut-admin__btn {
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--accent-primary);
  background: var(--bg-primary);
  color: var(--accent-primary);
  cursor: pointer;
}

.estrut-admin__btn:hover {
  background: color-mix(in srgb, var(--accent-primary) 10%, transparent);
}
</style>
